import { useQueryClient } from '@tanstack/react-query';
import { getGetCartQueryKey } from '@unity/commerce-bridge';
import { useCartManagement } from '@unity/commerce-core';
import { useCallback, useEffect, useState, useSyncExternalStore } from 'react';
import {
  readGuestCartId,
  rememberGuestCart,
  subscribeToGuestCart,
} from './guestCart';
import { useEnsureBridgeToken } from './useBridgeToken';

/**
 * - `none`: no guest cart to read, so nothing was minted.
 * - `pending`: the guest token is being provisioned.
 * - `ready`: the token is in place and the guest cart is still held.
 * - `failed`: no guest token could be provisioned.
 */
export type GuestCartAccessStatus = 'none' | 'pending' | 'ready' | 'failed';

export type GuestCartAccess = {
  status: GuestCartAccessStatus;
  retry: () => void;
};

/**
 * Gate for a commerce-core provider about to read a guest cart (CPS-7693):
 * provisions the guest token first. A lapsed guest identity is replaced by a
 * new one that forgets the old cart, which is why the provider must mount
 * only after this settles, reading the cart id as it is then.
 *
 * Follows the guest cart record, so a cart another tab creates, or one that
 * replaces the held cart, gates the provider's host again: `pending` unmounts
 * the provider and `ready` remounts it with that cart.
 */
export const useGuestCartAccess = ({
  enabled,
}: {
  enabled: boolean;
}): GuestCartAccess => {
  const ensure = useEnsureBridgeToken();
  const cartId = useSyncExternalStore(
    subscribeToGuestCart,
    readGuestCartId,
    () => undefined
  );
  const [attempt, setAttempt] = useState(0);
  const [settled, setSettled] = useState<{
    attempt: number;
    cartId: string;
    status: GuestCartAccessStatus;
  }>();
  const retry = useCallback(() => setAttempt((value) => value + 1), []);
  const isNeeded = enabled && Boolean(cartId);

  useEffect(() => {
    if (!isNeeded || !cartId) return;
    let active = true;
    void ensure().then((token) => {
      if (!active) return;
      let status: GuestCartAccessStatus = 'failed';
      if (token) status = readGuestCartId() ? 'ready' : 'none';
      setSettled({ attempt, cartId, status });
    });
    return () => {
      active = false;
      // A cart that comes back later is gated again, not served this outcome.
      setSettled(undefined);
    };
  }, [isNeeded, cartId, ensure, attempt]);

  if (!isNeeded) return { status: 'none', retry };
  return {
    // An outcome belongs to the cart it was settled for.
    status:
      settled?.attempt === attempt && settled.cartId === cartId
        ? settled.status
        : 'pending',
    retry,
  };
};

/**
 * commerce-core 0.24 stores the guest cart it creates, and the cart a currency
 * sync replaces it with, but its provider keeps the cart id it started with:
 * the next refetch would read a cart that is gone, or create yet another one.
 * This moves the provider and the stored id onto the cart commerce-core
 * holds, seeding that cart's query so it does not blank while it is re-read.
 * Must render inside CartManagementProvider and its QueryClientProvider.
 */
export const useFollowGuestCart = (): void => {
  const { cart, cartId, userId, enableAnonymousCart, setCartId } =
    useCartManagement();
  const queryClient = useQueryClient();
  const heldCartId = cart?.id;

  useEffect(() => {
    if (!enableAnonymousCart || userId || !heldCartId) return;
    rememberGuestCart(heldCartId);
    if (heldCartId === cartId) return;
    const held = queryClient.getQueryData(getGetCartQueryKey(cartId ?? ''));
    queryClient.setQueryData(getGetCartQueryKey(heldCartId), held);
    setCartId(heldCartId);
  }, [enableAnonymousCart, userId, heldCartId, cartId, queryClient, setCartId]);
};
